import React, { useState } from 'react';
import { useAttendance } from '../context/AttendanceContext';
import './Query.css';

const CATEGORIES = ['HR', 'IT Support', 'Payroll', 'Attendance', 'Leave Policy', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High'];

const PRIORITY_COLORS = {
  Low: { bg: 'rgba(34,211,165,0.1)', color: '#22d3a5', border: 'rgba(34,211,165,0.25)' },
  Medium: { bg: 'rgba(245,166,35,0.1)', color: '#f5a623', border: 'rgba(245,166,35,0.25)' },
  High: { bg: 'rgba(242,95,92,0.1)', color: '#f25f5c', border: 'rgba(242,95,92,0.25)' },
};

const STATUS_COLORS = {
  Open: { bg: 'rgba(79,142,247,0.1)', color: 'var(--accent-primary)', border: 'rgba(79,142,247,0.25)' },
  'In Progress': { bg: 'rgba(245,166,35,0.1)', color: '#f5a623', border: 'rgba(245,166,35,0.25)' },
  Resolved: { bg: 'rgba(34,211,165,0.1)', color: '#22d3a5', border: 'rgba(34,211,165,0.25)' },
};

export default function Query() {
  const { queries, submitQuery } = useAttendance();
  const [form, setForm] = useState({ subject: '', category: 'HR', priority: 'Medium', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.subject.trim()) e.subject = 'Subject is required';
    else if (form.subject.trim().length < 5) e.subject = 'Subject must be at least 5 characters';
    if (!form.message.trim()) e.message = 'Message is required';
    else if (form.message.trim().length < 20) e.message = 'Please provide more detail (min 20 characters)';
    return e;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    submitQuery(form);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setShowForm(false);
      setForm({ subject: '', category: 'HR', priority: 'Medium', message: '' });
      setErrors({});
    }, 1800);
  };

  const fmt = (iso) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="query-root fade-in-up">
      <div className="query-header-row">
        <div>
          <h2 className="query-title">My Queries</h2>
          <p className="query-subtitle">{queries.length} submitted · {queries.filter(q => q.status === 'Open').length} open</p>
        </div>
        <button className="query-new-btn" onClick={() => setShowForm(v => !v)}>
          {showForm ? (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Cancel
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              New Query
            </>
          )}
        </button>
      </div>

      {showForm && (
        <div className="query-form-card page-card fade-in-up">
          <p className="page-section-title">Submit a Query</p>
          <form onSubmit={handleSubmit} className="query-form" noValidate>
            <div className={`query-field ${errors.subject ? 'has-error' : ''}`}>
              <label className="query-label">Subject</label>
              <input
                type="text"
                className="query-input"
                placeholder="Brief description of your query"
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              />
              {errors.subject && <span className="query-err">{errors.subject}</span>}
            </div>

            <div className="query-form-row">
              <div className="query-field">
                <label className="query-label">Category</label>
                <select
                  className="query-select"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                >
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="query-field">
                <label className="query-label">Priority</label>
                <div className="priority-options">
                  {PRIORITIES.map(p => (
                    <button
                      key={p}
                      type="button"
                      className={`priority-btn ${form.priority === p ? 'active' : ''}`}
                      style={form.priority === p ? {
                        background: PRIORITY_COLORS[p].bg,
                        color: PRIORITY_COLORS[p].color,
                        border: `1px solid ${PRIORITY_COLORS[p].border}`,
                      } : {}}
                      onClick={() => setForm(f => ({ ...f, priority: p }))}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={`query-field ${errors.message ? 'has-error' : ''}`}>
              <label className="query-label">Message</label>
              <textarea
                className="query-textarea"
                rows={4}
                placeholder="Describe your query in detail..."
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              />
              {errors.message && <span className="query-err">{errors.message}</span>}
              <span className="query-char-count">{form.message.length} chars</span>
            </div>

            <div className="query-form-actions">
              <button
                type="submit"
                className={`query-submit-btn ${submitted ? 'done' : ''}`}
                disabled={submitted}
              >
                {submitted ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M4 9l4 4 7-8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Query Submitted!
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M15 3H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3l4 3 4-3h1a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round"/>
                    </svg>
                    Submit Query
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="query-list page-card delay-1 fade-in-up">
        {queries.length === 0 ? (
          <div className="query-empty">
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              <path d="M38 6H6a2 2 0 0 0-2 2v24a2 2 0 0 0 2 2h8l8 6 8-6h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2z" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M22 16v2M22 22v6" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>No queries submitted yet</p>
            <span>Use the button above to submit a query to HR or IT</span>
          </div>
        ) : (
          <div className="query-items">
            {queries.map((q, idx) => {
              const pc = PRIORITY_COLORS[q.priority] || PRIORITY_COLORS.Medium;
              const sc = STATUS_COLORS[q.status] || STATUS_COLORS.Open;
              return (
                <div key={q.id} className="query-item fade-in-up" style={{ animationDelay: `${idx * 0.04}s` }}>
                  <div className="query-item-top">
                    <div className="query-item-badges">
                      <span className="query-category-badge">{q.category}</span>
                      <span
                        className="query-priority-badge"
                        style={{ background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}
                      >
                        {q.priority}
                      </span>
                    </div>
                    <span
                      className="query-status-badge"
                      style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}
                    >
                      {q.status}
                    </span>
                  </div>
                  <h4 className="query-item-subject">{q.subject}</h4>
                  <p className="query-item-message">{q.message}</p>
                  <div className="query-item-meta">
                    Submitted on {fmt(q.submittedOn)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
